import axios from "axios";
import { BASE_URL } from "config/api";

// ==================== PROFIL UTILISATEUR (ME) ====================
export async function getMyUserProfile() {
  return axios.get(`${BASE_URL}/users/me`, {
    withCredentials: true,
  });
}

// Envoyer automatiquement les cookies (auth_token) avec les requêtes
axios.defaults.withCredentials = true;


// ==================== AUTH ====================
export async function login(email, mot_de_passe) {
  return axios.post(`${BASE_URL}/auth/login`, { email, mot_de_passe });
}

export async function verifyOtp(userId, otp) {
  return axios.post(`${BASE_URL}/auth/verify-otp`, { userId, otp });
}

export async function forgotPassword(telephone) {
  return axios.post(`${BASE_URL}/auth/forgot-password`, { telephone });
}

export async function verifyResetOtp(userId, otp) {
  return axios.post(`${BASE_URL}/auth/verify-reset-otp`, { userId, otp });
}

export async function resetPassword(userId, otp, nouveauMotDePasse, resetToken = null) {
  return axios.post(`${BASE_URL}/auth/reset-password`, { userId, otp, nouveauMotDePasse, resetToken });
}

export async function changePassword(ancienMotDePasse, nouveauMotDePasse) {
  return axios.post(`${BASE_URL}/auth/change-password`, { ancienMotDePasse, nouveauMotDePasse }, {
    withCredentials: true,
  });
}

export async function checkToken() {
  // Le backend lit le cookie httpOnly auth_token
  return axios.get(`${BASE_URL}/token/token`, {
    withCredentials: true,
  });
}

export async function logout() {
  // Appeler le backend pour supprimer le cookie httpOnly auth_token
  // Le backend gère la suppression du cookie, on ne peut pas le faire côté client
  try {
    await axios.post(`${BASE_URL}/auth/logout`, {}, {
      withCredentials: true,
    });
  } catch (error) {
    // Si l'endpoint n'existe pas, on continue quand même
    console.warn('[logout] Endpoint /auth/logout non disponible:', error.message);
  }
}

// ==================== USERS ====================
export async function getUsers() {
  return axios.get(`${BASE_URL}/users`, {
    withCredentials: true,
  });
}
export async function getUsersid(id) {
  return axios.get(`${BASE_URL}/users/${id}`, {
    withCredentials: true,
  });
}
export async function getUser() {
  // Le backend lit le cookie httpOnly auth_token dans /token/token
  // et renvoie les infos liées au token.
  try {
    const tokenInfo = await axios.get(`${BASE_URL}/token/token`, {
      withCredentials: true,
    });

    if (!tokenInfo.data || !tokenInfo.data.valid) {
      throw new Error("Session invalide ou expirée");
    }

    const data = tokenInfo.data;

    // 1) Si le backend renvoie déjà l'utilisateur complet, on le retourne directement
    if (data.user) {
      return data.user;
    }

    // 2) Sinon, on essaie de récupérer un id utilisateur dans la réponse
    const userId =
      data.userId ||
      data.id ||
      data.user_id ||
      (data.payload && (data.payload.id || data.payload.user_id));

    if (!userId) {
      console.error("[getUser] Impossible d'extraire l'id utilisateur depuis /token/token :", data);
      throw new Error("Impossible de récupérer l'id utilisateur depuis /token/token");
    }

    // 3) Puis on appelle /users/:id pour obtenir l'utilisateur
    const response = await axios.get(`${BASE_URL}/users/${userId}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("[getUser] Erreur lors de la récupération de l'utilisateur :", error);
    throw error;
  }
}

export async function updateUser(id, data) {
  const isFormData = data instanceof FormData;
  return axios.put(`${BASE_URL}/users/${id}`, data, {
    withCredentials: true,
    headers: {
      'Content-Type': isFormData ? 'multipart/form-data' : 'application/json'
    }
  });
}

export async function createUser(data) {
  return axios.post(`${BASE_URL}/users`, data, {
    withCredentials: true,
  });
}

export async function deleteUser(id) {
  return axios.delete(`${BASE_URL}/users/${id}`, {
    withCredentials: true,
  });
}

// ==================== MESSAGES ====================
export async function getMessages(missionId) {
  return axios.get(`${BASE_URL}/messages/${missionId}`, {
    withCredentials: true,
  });
}

export async function createMessage(data) {
  return axios.post(`${BASE_URL}/messages`, data, {
    withCredentials: true,
  });
}

export async function getUnreadMessagesCounts() {
  return axios.get(`${BASE_URL}/messages/unread/counts`, {
    withCredentials: true,
  });
}

export async function markMessageRead(messageId) {
  return axios.put(`${BASE_URL}/messages/read/${messageId}`, {}, {
    withCredentials: true,
  });
}

export async function markAllMessagesRead(missionId) {
  return axios.put(`${BASE_URL}/messages/read/all/${missionId}`, {}, {
    withCredentials: true,
  });
}

export async function deleteMessage(messageId) {
  return axios.delete(`${BASE_URL}/messages/${messageId}`, {
    withCredentials: true,
  });
}

// ==================== VEHICULES ====================
export async function getVehicules() {
  return axios.get(`${BASE_URL}/vehicules`, {
    withCredentials: true,
  });
}

export async function getVehicule(id) {
  return axios.get(`${BASE_URL}/vehicules/${id}`, {
    withCredentials: true,
  });
}

export async function updateVehicule(id, data) {
  return axios.put(`${BASE_URL}/vehicules/${id}`, data, {
    withCredentials: true,
  });
}

export async function updateVehiculesBatch(id, data) {
  // Pour la mise à jour d'un véhicule dans un batch (user_id ou infirmier)
  return axios.put(`${BASE_URL}/vehicules/${id}`, data, {
    withCredentials: true,
  });
}

export async function deleteVehicule(id) {
  return axios.delete(`${BASE_URL}/vehicules/${id}`, {
    withCredentials: true,
  });
}

export async function createVehicule(data) {
  return axios.post(`${BASE_URL}/vehicules`, data, {
    withCredentials: true,
  });
}

export async function getOxygenBottles(vehicleId) {
  return axios.get(`${BASE_URL}/vehicles/${vehicleId}/oxygen-bottles`, {
    withCredentials: true,
  });
}

// ==================== MATERIELS ====================
export async function getMateriels() {
  return axios.get(`${BASE_URL}/materiels`, {
    withCredentials: true,
  });
}

export async function getMaterielsByAmbulanceId(ambulanceId) {
  return axios.get(`${BASE_URL}/materiels/ambulance/${ambulanceId}`, {
    withCredentials: true,
  });
}

export async function createMateriel(data) {
  return axios.post(`${BASE_URL}/materiels`, data, {
    withCredentials: true,
  });
}

export async function updateMateriel(id, data) {
  return axios.put(`${BASE_URL}/materiels/${id}`, data, {
    withCredentials: true,
  });
}

export async function deleteMateriel(id) {
  return axios.delete(`${BASE_URL}/materiels/${id}`, {
    withCredentials: true,
  });
}

export async function incrementMateriel(id) {
  return axios.patch(`${BASE_URL}/materiels/${id}/increment`, {}, {
    withCredentials: true,
  });
}

export async function decrementMateriel(id) {
  return axios.patch(`${BASE_URL}/materiels/${id}/decrement`, {}, {
    withCredentials: true,
  });
}

export async function getUtilisationMateriel() {
  return axios.get(`${BASE_URL}/utilisation_materiel`, {
    withCredentials: true,
  });
}

export async function getUtilisationMaterielByAmbulanceId(ambulanceId) {
  return axios.get(`${BASE_URL}/utilisation_materiel/ambulance/${ambulanceId}`, {
    withCredentials: true,
  });
}

export async function patchMateriel(id, data) {
  return axios.patch(`${BASE_URL}/materiels/${id}`, data, {
    withCredentials: true,
  });
}

// ==================== PATIENTS ====================
export async function getPatients(page = 1, limit = 20, search = '') {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  if (search) params.append('search', search);

  return axios.get(`${BASE_URL}/patients?${params.toString()}`, {
    withCredentials: true,
  });
}

// Fonction pour récupérer tous les patients (utilisé pour les sélecteurs, etc.)
export async function getAllPatients() {
  return axios.get(`${BASE_URL}/patients?limit=10000`, {
    withCredentials: true,
  });
}

export async function getPatient(id) {
  return axios.get(`${BASE_URL}/patients/${id}`, {
    withCredentials: true,
  });
}

export async function updatePatient(id, data) {
  return axios.put(`${BASE_URL}/patients/${id}`, data, {
    withCredentials: true,
  });
}

export async function deletePatient(id) {
  return axios.delete(`${BASE_URL}/patients/${id}`, {
    withCredentials: true,
  });
}

export async function createPatient(data) {
  return axios.post(`${BASE_URL}/patients`, data, {
    withCredentials: true,
  });
}

export async function getClients() {
  return axios.get(`${BASE_URL}/clients`, {
    withCredentials: true,
  });
}

// ==================== RAPPORTS MEDICAUX ====================
export async function getRapportsMedicauxByPatient(patientId) {
  return axios.get(`${BASE_URL}/rapport-medicale/patient/${patientId}`, {
    withCredentials: true,
  });
}

// ==================== MISSIONS ====================
export async function getMissions() {
  return axios.get(`${BASE_URL}/missions`, {
    withCredentials: true,
  });
}

export async function getMission(id) {
  return axios.get(`${BASE_URL}/missions/${id}`, {
    withCredentials: true,
  });
}

// ==================== DISPATCH ====================
export async function createDispatch(data) {
  return axios.post(`${BASE_URL}/dispatches`, data, {
    withCredentials: true,
  });
}

// ==================== PROSPECTS ====================
// Ces endpoints utilisent désormais le cookie httpOnly auth_token (withCredentials)
export async function getProspects(params = {}) {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      queryParams.append(key, params[key]);
    }
  });

  const url = queryParams.toString()
    ? `${BASE_URL}/prospects?${queryParams.toString()}`
    : `${BASE_URL}/prospects`;

  return axios.get(url, {
    withCredentials: true,
  });
}

export async function getProspectById(id) {
  return axios.get(`${BASE_URL}/prospects/${id}`, {
    withCredentials: true,
  });
}

export async function createProspect(data) {
  return axios.post(`${BASE_URL}/prospects`, data, {
    withCredentials: true,
  });
}

export async function getProspectStats(params = {}) {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      queryParams.append(key, params[key]);
    }
  });

  const url = queryParams.toString()
    ? `${BASE_URL}/prospects/stats/overview?${queryParams.toString()}`
    : `${BASE_URL}/prospects/stats/overview`;

  return axios.get(url, {
    withCredentials: true,
  });
}

export async function getProspectOptions() {
  return axios.get(`${BASE_URL}/prospects/options`, {
    withCredentials: true,
  });
}

// ==================== MAINTENANCE ====================
export async function getMaintenanceTasks(vehicleId) {
  return axios.get(`${BASE_URL}/vehicles/${vehicleId}/maintenance-tasks`, {
    withCredentials: true,
  });
}

export async function createMaintenanceTask(vehicleId, data) {
  return axios.post(`${BASE_URL}/vehicles/${vehicleId}/maintenance-tasks`, data, {
    withCredentials: true,
  });
}

export async function updateMaintenanceTask(vehicleId, taskId, data) {
  return axios.put(`${BASE_URL}/vehicles/${vehicleId}/maintenance-tasks/${taskId}`, data, {
    withCredentials: true,
  });
}

export async function deleteMaintenanceTask(vehicleId, taskId) {
  return axios.delete(`${BASE_URL}/vehicles/${vehicleId}/maintenance-tasks/${taskId}`, {
    withCredentials: true,
  });
}

export async function createMaintenanceTaskHistory(vehicleId, taskId, data) {
  return axios.post(`${BASE_URL}/vehicles/${vehicleId}/maintenance-tasks/${taskId}/history`, data, {
    withCredentials: true,
  });
}

export async function getMaintenanceTaskHistory(vehicleId, taskId) {
  return axios.get(`${BASE_URL}/vehicles/${vehicleId}/maintenance-tasks/${taskId}/history`, {
    withCredentials: true,
  });
}

export async function getVehicleMaintenanceHistory(vehicleId) {
  return axios.get(`${BASE_URL}/vehicles/${vehicleId}/maintenance-history`, {
    withCredentials: true,
  });
}
// ==================== POINTAGES ====================
export async function getAllPointagesToday(date) {
  const params = date ? `?date=${date}` : '';
  return axios.get(`${BASE_URL}/pointages${params}`, {
    withCredentials: true,
  });
}

export async function getPointagesByUser(userId) {
  return axios.get(`${BASE_URL}/pointages/${userId}`, {
    withCredentials: true,
  });
}

// ==================== STOCK CENTRAL ====================
export async function getAllStock() {
  return axios.get(`${BASE_URL}/stock`, {
    withCredentials: true,
  });
}

export async function createStock(data) {
  return axios.post(`${BASE_URL}/stock`, data, {
    withCredentials: true,
  });
}

export async function updateStock(id, data) {
  return axios.put(`${BASE_URL}/stock/${id}`, data, {
    withCredentials: true,
  });
}

export async function deleteStock(id) {
  return axios.delete(`${BASE_URL}/stock/${id}`, {
    withCredentials: true,
  });
}

export async function getStockStats() {
  return axios.get(`${BASE_URL}/stock/stats`, {
    withCredentials: true,
  });
}

export async function assignStockToAmbulance(data) {
  return axios.post(`${BASE_URL}/stock/assign`, data, {
    withCredentials: true,
  });
}

export async function returnMaterielToStock(data) {
  return axios.post(`${BASE_URL}/stock/return`, data, {
    withCredentials: true,
  });
}

// ==================== ANALYSES / PERFORMANCES ====================
export async function getUserPerformance(userId) {
  return axios.get(`${BASE_URL}/analyses/perf_user/${userId}`, {
    withCredentials: true,
  });
}

// ==================== SITES ====================
export async function createSite(siteData) {
  return axios.post(`${BASE_URL}/sites`, siteData, {
    withCredentials: true,
  });
}

export async function getSites() {
  return axios.get(`${BASE_URL}/sites`, {
    withCredentials: true,
  });
}

export async function getSiteById(siteId) {
  return axios.get(`${BASE_URL}/sites/${siteId}`, {
    withCredentials: true,
  });
}

export async function updateSite(siteId, siteData) {
  return axios.put(`${BASE_URL}/sites/${siteId}`, siteData, {
    withCredentials: true,
  });
}

export async function deleteSite(siteId) {
  return axios.delete(`${BASE_URL}/sites/${siteId}`, {
    withCredentials: true,
  });
}

// ==================== USER SITES ====================
export async function assignUserToSite(userData) {
  return axios.post(`${BASE_URL}/usersites`, userData, {
    withCredentials: true,
  });
}

export async function getUserSites(userId) {
  return axios.get(`${BASE_URL}/users/${userId}/sites`, {
    withCredentials: true,
  });
}

export async function getSiteUsers(siteId) {
  return axios.get(`${BASE_URL}/sites/${siteId}/users`, {
    withCredentials: true,
  });
}

export async function removeUserFromSite(userSiteId) {
  return axios.delete(`${BASE_URL}/usersites/${userSiteId}`, {
    withCredentials: true,
  });
}

export async function bulkAssignUsersToSite(data) {
  return axios.post(`${BASE_URL}/usersites/bulk`, data, {
    withCredentials: true,
  });
}

export async function changeUserSite(userId, data) {
  return axios.put(`${BASE_URL}/usersites/user/${userId}/site`, data, {
    withCredentials: true,
  });
}

export async function getAllUsersWithSites() {
  return axios.get(`${BASE_URL}/usersites/all-with-sites`, {
    withCredentials: true,
  });
}